import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { EstadoInforme, EstadoInformePagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchEstadoInforme = ({
  page = 1,
  nombre = '',
  page_size = 10,
}: GetEstadoInformeParams) => {
  return useQuery({
    queryKey: ['estadosInforme', page, nombre],
    queryFn: async () => {
      return getEstadosInforme({ page, nombre, page_size });
    },
  });
};

export const useGetEstadoInforme = (id: string) => {
  return useQuery({
    queryKey: ['estadosInforme', id],
    queryFn: async () => {
      return getEstadoInforme(id);
    },
  });
};

export const useCreateEstadoInforme = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEstadoInforme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estadosInforme'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateEstadoInforme = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateEstadoInforme,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['estadosInforme'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetEstadoInformeParams = {
  page?: number;
  nombre?: string;
  page_size?: number;
};

export type CreateEstadoInformeParams = Omit<
  EstadoInforme,
  'id_estado_informe'
>;
export type UpdateEstadoInformeParams = {
  id: string;
  data: CreateEstadoInformeParams;
};

export const getEstadosInforme = async (params?: GetEstadoInformeParams) => {
  const { page = 1, nombre = '', page_size = 10 } = params || {};

  return get<EstadoInformePagingRes>(
    `${urlBackend}api/v1/estados-informes/?page=${page}&nombre=${nombre}&page_size=${page_size}`,
    true
  );
};

export const getEstadoInforme = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<EstadoInforme>(
    `${urlBackend}api/v1/estados-informes/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createEstadoInforme = async (data: CreateEstadoInformeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<EstadoInforme>(
    `${urlBackend}api/v1/estados-informes/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateEstadoInforme = async ({
  data,
  id,
}: UpdateEstadoInformeParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<EstadoInforme>(
    `${urlBackend}api/v1/estados-informes/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
